import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'react-query';
import { ValidDataService } from 'service/validDataService';
import { useLocation } from 'hooks/useLocation';

const escapeCsvValue = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  const stringValue = String(value);
  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const buildFailedRowsCsv = (failedRows = []) => {
  if (!failedRows.length) {
    return '';
  }

  const sourceColumns = [];
  failedRows.forEach(({ sourceRow = {} }) => {
    Object.keys(sourceRow || {}).forEach((key) => {
      if (!sourceColumns.includes(key)) {
        sourceColumns.push(key);
      }
    });
  });

  const headers = ['rowNumber', 'errorReason', ...sourceColumns];
  const csvLines = [headers.map(escapeCsvValue).join(',')];

  failedRows.forEach(({ rowNumber, errorReason, sourceRow = {} }) => {
    const rowValues = [
      rowNumber,
      errorReason,
      ...sourceColumns.map((column) => sourceRow?.[column] ?? '')
    ];
    csvLines.push(rowValues.map(escapeCsvValue).join(','));
  });

  return csvLines.join('\n');
};

const downloadFailedRowsCsv = (failedRows = []) => {
  const csvContent = buildFailedRowsCsv(failedRows);
  if (!csvContent) {
    return;
  }

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `frame-import-failures-${timestamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const InventoryImportResultDialog = ({ open, onClose, result }) => {
  if (!result) return null;

  const {
    importedCount = 0,
    updatedCount = 0,
    skippedCount = 0,
    errors = [],
    failedRows = [],
  } = result;

  const failedCount = failedRows.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Inventory Import Results</DialogTitle>
      <DialogContent>
        <Alert severity={errors.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
          Imported {importedCount}, updated {updatedCount}, skipped {skippedCount}
        </Alert>
        {failedCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip label={`${failedCount} failed`} color="error" size="small" />
            <Button variant="outlined" size="small" onClick={() => downloadFailedRowsCsv(failedRows)}>
              Download Failed Rows CSV
            </Button>
          </Box>
        )}
        {errors.length > 0 && (
          <Box>
            {errors.map((error, index) => (
              <Typography key={`${error}-${index}`} variant="body2" color="error" sx={{ mb: 0.5 }}>
                {error}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const InventoryFrameImportDialog = ({ open, onClose, onImported }) => {
  const { availableLocations, selectedLocation } = useLocation();
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [error, setError] = useState('');
  const [duplicateMode, setDuplicateMode] = useState('skip');
  const [importResult, setImportResult] = useState(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');

  const { data: frameImportFieldsResponse, isLoading: frameImportFieldsLoading, isError: frameImportFieldsLoadFailed } = useQuery(
    ['frame-import-fields'],
    ValidDataService.getFrameImportFields,
    {
      enabled: open,
      staleTime: 5 * 60 * 1000,
    }
  );

  const modelFields = useMemo(() => frameImportFieldsResponse?.data || [], [frameImportFieldsResponse]);

  const resetState = () => {
    setFile(null);
    setColumns([]);
    setMapping({});
    setPreviewData([]);
    setError('');
    setDuplicateMode('skip');
    setSelectedLocationId(selectedLocation?.id ? String(selectedLocation.id) : '');
  };

  const downloadTemplate = useCallback(() => {
    if (!modelFields.length) return;

    const headers = modelFields.map((f) => escapeCsvValue(f.label || f.key));
    const sampleRow = modelFields.map((f) => {
      if (f.example !== undefined && f.example !== null) return escapeCsvValue(String(f.example));
      if (f.required) return escapeCsvValue(`<${f.label || f.key}>`);
      return '';
    });

    const csv = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'frame-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [modelFields]);

  const onDrop = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setColumns([]);
    setMapping({});
    setPreviewData([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let parsedColumns = [];
        let data = [];

        if (selectedFile.name.endsWith('.csv')) {
          const csv = Papa.parse(e.target.result, { header: true, skipEmptyLines: true });
          parsedColumns = csv.meta.fields || [];
          data = csv.data.slice(0, 5);
        } else {
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          if (jsonData.length > 0) {
            parsedColumns = jsonData[0];
            data = jsonData.slice(1, 6).map((row) => {
              const obj = {};
              parsedColumns.forEach((col, idx) => {
                obj[col] = row[idx] || '';
              });
              return obj;
            });
          }
        }

        setColumns(parsedColumns);
        setPreviewData(data);

        const initialMapping = {};
        parsedColumns.forEach((col) => {
          const normalizedCol = String(col).toLowerCase().trim().replace(/[^a-z0-9]/g, '');
          const matchedField = modelFields.find((field) => (
            [field.key, field.label, ...(field.aliases || [])]
              .filter(Boolean)
              .some((candidate) => String(candidate).toLowerCase().trim().replace(/[^a-z0-9]/g, '') === normalizedCol)
          ));
          if (matchedField) {
            initialMapping[col] = matchedField.key;
          }
        });
        setMapping(initialMapping);
      } catch (parseError) {
        setError('Error parsing file. Please check the file format.');
        console.error(parseError);
      }
    };

    if (selectedFile.name.endsWith('.csv')) {
      reader.readAsText(selectedFile);
    } else {
      reader.readAsBinaryString(selectedFile);
    }
  }, [modelFields]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  const importMutation = useMutation(
    async ({ importFile, importMapping, mode }) => {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('mapping', JSON.stringify(importMapping));
      formData.append('duplicateMode', mode);
      const effectiveLocationId = selectedLocationId || (selectedLocation ? String(selectedLocation.id) : '');
      formData.append('locationId', effectiveLocationId);
      return ValidDataService.importFrameInventory(formData);
    },
    {
      onSuccess: (response) => {
        setImportResult(response.data || response);
        setResultDialogOpen(true);
        onImported && onImported();
      },
      onError: (importError) => {
        toast.error(importError.message || 'Inventory import failed');
      }
    }
  );

  const handleImport = () => {
    const effectiveLocationId = selectedLocationId || (selectedLocation ? String(selectedLocation.id) : '');
    if (!effectiveLocationId) {
      setError('Please select a location to import frames into.');
      return;
    }

    if (!file) {
      setError('Please select a file to import.');
      return;
    }

    importMutation.mutate({ importFile: file, importMapping: mapping, mode: duplicateMode });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleResultClose = () => {
    setResultDialogOpen(false);
    setImportResult(null);
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Import Frame Inventory</DialogTitle>
        <DialogContent>
          {frameImportFieldsLoading && !modelFields.length ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Loading import field definitions...</Typography>
            </Box>
          ) : frameImportFieldsLoadFailed ? (
            <Alert severity="error" sx={{ my: 2 }}>
              Unable to load import field definitions. Please try again.
            </Alert>
          ) : !file ? (
            <>
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragActive ? '#f0f0f0' : 'transparent',
                  '&:hover': { bgcolor: '#f5f5f5' }
                }}
              >
                <input {...getInputProps()} />
                <Typography variant="h6" gutterBottom>
                  {isDragActive ? 'Drop the file here...' : 'Drag & drop a CSV or Excel file here, or click to select'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Supported formats: .csv, .xlsx, .xls
                </Typography>
              </Box>
              {modelFields.length > 0 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Tooltip title="Download a CSV template with all accepted column headers and a sample row">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={downloadTemplate}
                    >
                      Download Template
                    </Button>
                  </Tooltip>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    Not sure about the format? Download a template to get started.
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                File: {file.name}
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <FormControl fullWidth size="small" sx={{ my: 2, maxWidth: 320 }}>
                <InputLabel>Import to Location *</InputLabel>
                <Select
                  value={selectedLocationId || (selectedLocation ? String(selectedLocation.id) : '')}
                  label="Import to Location *"
                  onChange={(event) => setSelectedLocationId(event.target.value)}
                >
                  <MenuItem value=""><em>Select a location</em></MenuItem>
                  {availableLocations.map((loc) => (
                    <MenuItem key={loc.id} value={String(loc.id)}>{loc.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ my: 2, maxWidth: 260 }}>
                <InputLabel>Duplicate Handling</InputLabel>
                <Select
                  value={duplicateMode}
                  label="Duplicate Handling"
                  onChange={(event) => setDuplicateMode(event.target.value)}
                >
                  <MenuItem value="skip">Skip duplicates</MenuItem>
                  <MenuItem value="update">Update existing rows</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="h6" gutterBottom>Column Mapping</Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>File Column</TableCell>
                      <TableCell>Map to Field</TableCell>
                      <TableCell>Required</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {columns.map((column) => (
                      <TableRow key={column}>
                        <TableCell>{column}</TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <InputLabel>Inventory Field</InputLabel>
                            <Select
                              value={mapping[column] || ''}
                              label="Inventory Field"
                              onChange={(event) => setMapping((prev) => ({
                                ...prev,
                                [column]: event.target.value
                              }))}
                            >
                              <MenuItem value=""><em>Skip</em></MenuItem>
                              {modelFields.map((field) => (
                                <MenuItem key={field.key} value={field.key}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {field.label}
                                    {field.required && (
                                      <Chip label="Required" size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                    )}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {modelFields.find((field) => field.key === mapping[column])?.required
                            ? <Chip label="Required" size="small" color="error" variant="filled" />
                            : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {previewData.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Preview</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {columns.map((column) => <TableCell key={column}>{column}</TableCell>)}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.map((row, index) => (
                          <TableRow key={index}>
                            {columns.map((column) => <TableCell key={column}>{row[column]}</TableCell>)}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleImport} variant="contained" disabled={!file || importMutation.isLoading}>
            {importMutation.isLoading ? <CircularProgress size={20} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      <InventoryImportResultDialog
        open={resultDialogOpen}
        onClose={handleResultClose}
        result={importResult}
      />
    </>
  );
};

export default InventoryFrameImportDialog;